/// <amd-module name="Artifacts/Feed/hooks/InfiniteScrolling" />
import * as React from "react";
import { useEffect, useRef } from "react";
import { TimerManagement } from "VSS/Core/TimerManagement";

/**
 * useInfiniteScrolling is a hook infinite scroll on a div element.
 * @param loadNext callback that executes when div element reach scroll trigger.
 * @param sensitivity defines how early callback will execute before reaching bottom of the scrolling.
 * @param delay defines minimal delay in milliseconds between callback executions.
 * @returns React Ref object that should be attached to scrollable div.
 */
export function useInfiniteScrolling(
  loadNext: () => void,
  sensitivity = 150,
  delay = 1000,
): React.MutableRefObject<HTMLDivElement | null> {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Debounce package loading to prevent multiple requests.
    const timerManagement = new TimerManagement();
    const debouncedCall = timerManagement.debounce(loadNext, delay);
    const ref = scrollRef.current;

    const onScroll = () => {
      if (ref) {
        if (ref.scrollHeight - ref.scrollTop - ref.offsetHeight < sensitivity) {
          debouncedCall();
        }
      }
    };

    ref?.addEventListener("scroll", onScroll);

    return () => {
      ref?.removeEventListener("scroll", onScroll);
    };
  }, [loadNext, sensitivity, delay]);

  return scrollRef;
}
